import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import BuildIcon from "@mui/icons-material/Build";
import SpeedIcon from "@mui/icons-material/Speed";
import ExtensionIcon from "@mui/icons-material/Extension";

const AITeacherToolbox = () => {
  const [openImage, setOpenImage] = useState(null);

  // Project data
  const project = {
    title: "AI Teacher Toolbox",
    overview:
      "I developed an AI Toolbox containing nearly 30 specialized tools designed to assist teachers with their daily tasks. This system was rapidly developed to establish a competitive position against core market competitors while addressing real teacher needs.",
    images: [
      {
        src: "/images/teacher_toolbox/tool_box.png",
        title: "Complete AI Teacher Toolbox",
        description:
          "Overview of the comprehensive collection of AI tools for educators",
      },
      {
        src: "/images/teacher_toolbox/full_dialogue.png",
        title: "Dialogue Creation Tool",
        description:
          "AI-powered tool for generating realistic dialogue scenarios for language learning",
      },
      {
        src: "/images/teacher_toolbox/create_listening.png",
        title: "Listening Exercise Generator",
        description:
          "Tool that creates customized listening comprehension activities",
      },
      {
        src: "/images/teacher_toolbox/correct_questions.png",
        title: "Question Correction Tool",
        description:
          "Helps teachers quickly correct and improve student assessment questions",
      },
    ],
    keyFeatures: [
      {
        title: "Comprehensive Tool Collection",
        description:
          "Nearly 30 different AI-powered tools specifically designed for educators",
        icon: <BuildIcon fontSize="large" />,
      },
      {
        title: "Needs-Based Design",
        description:
          "All tools were developed based on direct teacher feedback and needs analysis",
        icon: <SpeedIcon fontSize="large" />,
      },
      {
        title: "Dynamic No-Code System",
        description:
          "Created a generalized tool framework that allows for rapid deployment without requiring custom coding for each tool",
        icon: <ExtensionIcon fontSize="large" />,
      },
      {
        title: "Real-Time Updates",
        description:
          "System allows prompt updates and modifications without developer intervention",
        icon: <SpeedIcon fontSize="large" />,
      },
    ],
    moduleFeatures: [
      "Adjustable system prompts",
      "Model selection options",
      "Temperature control for output variability",
    ],
    technicalImplementation:
      "Instead of developing each tool as a separate application requiring individual coding efforts, I designed a unified framework that allows new tools to be created through a no-code data entry system. This approach dramatically reduced development time and resources while maintaining high quality and functionality.",
    challenges: [
      "Time Constraints: Completed the entire project in just 2 weeks to meet competitive market demands",
      "Scale Requirements: Designed a system capable of supporting dozens of distinct tools without proportional development time",
      "Flexibility vs. Standardization: Created a framework that balances the need for consistent user experience with the unique requirements of each tool",
    ],
    impact:
      "The AI Teacher Toolbox has enabled our platform to quickly match and exceed competitor offerings while providing educators with practical, time-saving solutions. The modular design ensures we can continue expanding the toolbox with minimal development overhead, responding rapidly to emerging teacher needs.",
  };

  // Image modal handlers
  const handleImageClick = (img) => setOpenImage(img);
  const handleClose = () => setOpenImage(null);

  // Component for displaying an image card with description
  const ImageCard = ({ image, title, description }) => (
    <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
      <Box sx={{ cursor: "pointer" }} onClick={() => handleImageClick(image)}>
        <img
          src={image.src}
          alt={image.title}
          style={{
            width: "100%",
            height: 260,
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
      </Box>
      <Typography variant="h6" sx={{ mt: 2 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {description}
      </Typography>
    </Paper>
  );

  return (
    <>
      {/* Image Lightbox Dialog */}
      <Dialog open={!!openImage} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#111" }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          {openImage && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
              }}
            >
              <img
                src={openImage.src}
                alt={openImage.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  width: "auto",
                  height: "auto",
                  borderRadius: 8,
                  boxShadow: "0 4px 32px #000a",
                  background: "#222",
                }}
              />
              <Typography variant="h6" color="#fff" sx={{ mt: 2 }}>
                {openImage.title}
              </Typography>
              <Typography variant="body2" color="#ccc" sx={{ mt: 1, mb: 1 }}>
                {openImage.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Overview */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {project.title}
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
          {project.overview}
        </Typography>
      </Paper>

      {/* Toolbox Overview */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Toolbox Overview
        </Typography>
        <Paper elevation={2} sx={{ p: 2 }}>
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => handleImageClick(project.images[0])}
          >
            <img
              src={project.images[0].src}
              alt={project.images[0].title}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: 400,
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </Box>
          <Typography variant="body1" sx={{ mt: 2, p: 2 }}>
            The AI Teacher Toolbox offers nearly 30 specialized tools designed
            to streamline various teaching tasks. From content creation to
            assessment generation, these tools help educators save time and
            enhance their teaching materials.
          </Typography>
        </Paper>
      </Box>

      {/* Featured Tools */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Featured Tools
        </Typography>
        <Grid container spacing={3}>
          {project.images.slice(1).map((image, index) => (
            <Grid item xs={12} md={4} key={index}>
              <ImageCard
                image={image}
                title={image.title}
                description={image.description}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Key Features Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Key Features
        </Typography>
        <Grid container spacing={3}>
          {project.keyFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ mb: 2, color: "primary.main" }}>{feature.icon}</Box>
                <Typography variant="h6" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body1">{feature.description}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Modular Architecture */}
      <Paper elevation={3} sx={{ p: 4, mb: 5 }}>
        <Typography variant="h5" gutterBottom>
          Modular Architecture
        </Typography>
        <Typography variant="body1" paragraph sx={{ mb: 3 }}>
          The toolbox features a flexible system with various input types and
          customizable parameters:
        </Typography>
        <Grid container spacing={2}>
          {project.moduleFeatures.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                }}
              >
                <Typography variant="h6" align="center">
                  {feature}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Technical Implementation & Challenges */}
      <Grid container spacing={4} sx={{ mb: 5 }}>
        {/* Technical Implementation */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: "100%" }}>
            <Typography variant="h5" gutterBottom>
              Technical Implementation
            </Typography>
            <Typography variant="body1" paragraph>
              {project.technicalImplementation}
            </Typography>
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="h6" gutterBottom>
                No-Code Framework Benefits
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="90% reduction in development time" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Consistent user experience across all tools" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Rapid iteration based on user feedback" />
                </ListItem>
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* Challenges Overcome */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: "100%" }}>
            <Typography variant="h5" gutterBottom>
              Challenges Overcome
            </Typography>
            <List>
              {project.challenges.map((challenge, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={challenge}
                    primaryTypographyProps={{ variant: "body1" }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Impact and Results */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Impact and Results
        </Typography>
        <Typography variant="body1" paragraph>
          {project.impact}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "success.light",
                  color: "success.contrastText",
                }}
              >
                <Typography variant="h3" gutterBottom>
                  30
                </Typography>
                <Typography variant="h6">AI Tools</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "info.light",
                  color: "info.contrastText",
                }}
              >
                <Typography variant="h3" gutterBottom>
                  2
                </Typography>
                <Typography variant="h6">Weeks Development</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "warning.light",
                  color: "warning.contrastText",
                }}
              >
                <Typography variant="h3" gutterBottom>
                  0
                </Typography>
                <Typography variant="h6">Custom Code Per Tool</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </>
  );
};

export default AITeacherToolbox;
